import { getLeadSource } from '../lib/leadSource';
import React, { useState } from 'react';
import * as Icons from 'lucide-react';
import { api } from '../lib/api';
import { useRegion } from '../contexts/RegionContext';
import { useLang } from '../contexts/LanguageContext';
import { SERVICES_KEYWORDS } from '../mock/serviceKeywords';

const PROJECT_SIZES = ['Small (<$10k)', 'Medium ($10k-$50k)', 'Large ($50k-$250k)', 'Enterprise ($250k+)', 'Not sure yet'];
const TIMELINES = ['Urgent (<2 weeks)', '1-3 months', '3-6 months', '6+ months', 'Ongoing partnership'];
const CONTACT_PREFS = [
  { val: 'email',    label: 'Email',      icon: 'Mail' },
  { val: 'phone',    label: 'Phone Call', icon: 'Phone' },
  { val: 'whatsapp', label: 'WhatsApp',   icon: 'MessageCircle' },
  { val: 'video',    label: 'Video Call', icon: 'Video' }
];

const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);

/* -------- shared chip + pill list -------- */

const ContactChip = ({ id, label, active, onClick }) => (
  <button key={id} type="button" onClick={onClick}
    className={`text-xs px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${active ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-900/50 text-gray-300 hover:border-green-500'}`}>
    <span className={`w-3 h-3 rounded-sm border ${active ? 'bg-orange-500 border-orange-500' : 'border-gray-500'} flex items-center justify-center`}>
      {active && <Icons.Check className="w-2 h-2 text-white" />}
    </span>
    {label}
  </button>
);

const PillList = ({ label, options, value, onChange }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-green-400 font-bold">{label}</label>
    <div className="mt-2 grid grid-cols-1 gap-1.5">
      {options.map(p => (
        <button key={p} type="button" onClick={() => onChange(p)}
          className={`text-left px-3 py-2 rounded text-xs border transition-colors ${value === p ? 'bg-gray-900/40 border-gray-500 text-green-200' : 'border-gray-900/40 text-gray-400 hover:border-green-700'}`}>
          {p}
        </button>
      ))}
    </div>
  </div>
);

const ProgressBar = ({ step, setStep }) => (
  <div className="flex items-center justify-between mb-8">
    {[{ n: 1, label: 'Select Services' }, { n: 2, label: 'Sub-Services & Details' }, { n: 3, label: 'Your Info' }].map((s, i) => (
      <React.Fragment key={s.n}>
        <button onClick={() => s.n < step && setStep(s.n)} className="flex items-center gap-2.5 group disabled:cursor-not-allowed" disabled={s.n > step}>
          <span className={`w-9 h-9 rounded-full flex items-center justify-center font-black text-sm transition-all ${step >= s.n ? 'bg-orange-500 text-white' : 'bg-[#1a1f2e] border border-gray-900/50 text-gray-500'}`}>
            {step > s.n ? <Icons.Check className="w-4 h-4" /> : s.n}
          </span>
          <span className={`hidden sm:inline text-[11px] uppercase tracking-wider font-bold ${step >= s.n ? 'text-green-400' : 'text-gray-500'}`}>{s.label}</span>
        </button>
        {i < 2 && <div className={`flex-1 h-0.5 mx-2 ${step > s.n ? 'bg-gray-500' : 'bg-gray-900/40'}`} />}
      </React.Fragment>
    ))}
  </div>
);

/* -------- Step 1: Services -------- */
const Step1 = ({ services, setServices, setStep }) => (
  <div data-testid="contact-step-1-content">
    <div className="text-green-400 text-[12px] uppercase tracking-[0.25em] font-bold mb-2">Step 1 of 3</div>
    <h3 className="text-2xl md:text-3xl font-black">Which services do you need? <span className="text-orange-400">*</span></h3>
    <p className="text-gray-400 text-sm mt-2">Tick all that apply. You can pick multiple disciplines.</p>
    <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[460px] overflow-y-auto pr-2">
      {SERVICES_KEYWORDS.map(s => {
        const active = services.includes(s.slug);
        return (
          <button key={s.slug} type="button" onClick={() => toggle(services, setServices, s.slug)}
            data-testid={`contact-svc-${s.slug}`}
            className={`text-left p-4 rounded border-2 transition-all ${active ? 'bg-gray-900/30 border-gray-500' : 'bg-[#1a1f2e] border-gray-900/40 hover:border-green-700'}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="font-bold text-sm">{s.name}</div>
              <div className={`w-5 h-5 rounded-sm border-2 flex items-center justify-center shrink-0 ${active ? 'bg-orange-500 border-orange-500' : 'border-gray-600'}`}>
                {active && <Icons.Check className="w-3 h-3 text-white" />}
              </div>
            </div>
            <div className="text-[11px] text-gray-400 mt-1.5">{s.subServices.length} sub-services available</div>
          </button>
        );
      })}
    </div>
    <div className="flex items-center justify-between mt-6">
      <div className="text-xs text-green-400"><span className="text-gray-400 font-bold">{services.length}</span> service{services.length !== 1 && 's'} selected</div>
      <button onClick={() => setStep(2)} disabled={!services.length} data-testid="contact-next-1" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold px-6 py-3 rounded uppercase text-xs tracking-wider">
        Next <Icons.ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  </div>
);

/* -------- Step 2: Sub-services + scope -------- */
const Step2 = ({ services, subs, setSubs, size, setSize, timeline, setTimeline, setStep }) => {
  const selectedServiceObjs = SERVICES_KEYWORDS.filter(s => services.includes(s.slug));
  return (
    <div>
      <div className="text-green-400 text-[12px] uppercase tracking-[0.25em] font-bold mb-2">Step 2 of 3</div>
      <h3 className="text-2xl md:text-3xl font-black">Pick sub-services &amp; share details</h3>
      <p className="text-gray-400 text-sm mt-2">Suggested sub-services for the disciplines you chose.</p>

      <div className="mt-6 space-y-5 max-h-[400px] overflow-y-auto pr-2">
        {selectedServiceObjs.map(s => (
          <div key={s.slug} className="bg-[#1a1f2e] border border-gray-900/40 rounded p-4">
            <div className="text-orange-400 text-[10px] uppercase tracking-wider font-bold mb-2 flex items-center gap-1.5">
              <Icons.Layers className="w-3 h-3" /> {s.name}
            </div>
            <div className="flex flex-wrap gap-2">
              {s.subServices.map(sub => {
                const id = `${s.slug}::${sub}`;
                return <ContactChip key={id} id={id} label={sub} active={subs.includes(id)} onClick={() => toggle(subs, setSubs, id)} />;
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <PillList label="Project Size" options={PROJECT_SIZES} value={size} onChange={setSize} />
        <PillList label="Timeline" options={TIMELINES} value={timeline} onChange={setTimeline} />
      </div>

      <div className="flex items-center justify-between mt-6">
        <button onClick={() => setStep(1)} data-testid="contact-back-2" className="inline-flex items-center gap-2 text-gray-400 hover:text-green-400 text-xs font-bold uppercase tracking-wider"><Icons.ArrowLeft className="w-3.5 h-3.5" /> Back</button>
        <div className="text-xs text-green-400"><span className="text-gray-400 font-bold">{subs.length}</span> sub-service{subs.length !== 1 && 's'}</div>
        <button onClick={() => setStep(3)} disabled={!services.length} data-testid="contact-next-2" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white font-bold px-6 py-3 rounded uppercase text-xs tracking-wider">
          Next <Icons.ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

/* -------- Step 3: Contact + submit -------- */
const CONTACT_FIELDS = [
  ['firstName', 'First Name',   true,  'text'],
  ['lastName',  'Last Name',    false, 'text'],
  ['email',     'Email Address', true,  'email'],
  ['phone',     'Phone',         false, 'tel'],
  ['company',   'Company',       false, 'text'],
  ['website',   'Website',       false, 'text']
];

const Step3 = ({ summary, pref, setPref, errMsg, loading, onBack, onSubmit }) => (
  <form onSubmit={onSubmit} data-testid="contact-step-3-content">
    <div className="text-green-400 text-[12px] uppercase tracking-[0.25em] font-bold mb-2">Step 3 of 3</div>
    <h3 className="text-2xl md:text-3xl font-black">Your information</h3>
    <p className="text-gray-400 text-sm mt-2">We&apos;ll reach out within 24 hours via your preferred channel.</p>

    <div className="mt-6 grid sm:grid-cols-2 gap-4">
      {CONTACT_FIELDS.map(([n, l, req, ty]) => (
        <div key={n}>
          <label className="text-xs uppercase tracking-wider text-green-400 font-bold">{l}{req && ' *'}</label>
          <input name={n} type={ty} required={req} data-testid={`contact-${n}`} className="mt-2 w-full bg-[#1a1f2e] border border-gray-900/50 rounded px-4 py-3 text-sm focus:outline-none focus:border-gray-500" />
        </div>
      ))}
      <div className="sm:col-span-2">
        <label className="text-xs uppercase tracking-wider text-green-400 font-bold">Additional Details</label>
        <textarea name="req" required rows={4} data-testid="contact-requirement" className="mt-2 w-full bg-[#1a1f2e] border border-gray-900/50 rounded px-4 py-3 text-sm focus:outline-none focus:border-gray-500" placeholder="Brief project description, any specific requirements, locations, deadlines..."></textarea>
      </div>
      <div className="sm:col-span-2">
        <label className="text-xs uppercase tracking-wider text-green-400 font-bold">Preferred Contact Method</label>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {CONTACT_PREFS.map(p => {
            const Ic = Icons[p.icon];
            const active = pref === p.val;
            return (
              <button key={p.val} type="button" onClick={() => setPref(p.val)}
                data-testid={`contact-pref-${p.val}`}
                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded border text-xs font-bold uppercase tracking-wider transition-colors ${active ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-900/40 text-gray-400 hover:border-green-700'}`}>
                <Ic className="w-3.5 h-3.5" /> {p.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>

    <div className="mt-6 p-4 bg-[#1a1f2e] border border-gray-900/40 rounded text-xs space-y-1">
      <div className="text-orange-400 font-bold uppercase tracking-wider mb-2">Your selections</div>
      <div><span className="text-gray-400">Services:</span> <span className="text-green-300">{summary.servicesCount || 'none'}</span></div>
      <div><span className="text-gray-400">Sub-services:</span> <span className="text-green-300">{summary.subsCount || 'none'}</span></div>
      {summary.size && <div><span className="text-gray-400">Size:</span> <span className="text-green-300">{summary.size}</span></div>}
      {summary.timeline && <div><span className="text-gray-400">Timeline:</span> <span className="text-green-300">{summary.timeline}</span></div>}
      <div><span className="text-gray-400">Contact via:</span> <span className="text-green-300">{pref}</span></div>
    </div>

    {errMsg && <div className="mt-4 text-red-400 text-xs bg-red-900/20 border border-red-700/40 rounded px-3 py-2 flex items-center gap-2"><Icons.AlertCircle className="w-4 h-4" /> {errMsg}</div>}

    <div className="flex items-center justify-between mt-6">
      <button type="button" onClick={onBack} data-testid="contact-back-3" className="inline-flex items-center gap-2 text-gray-400 hover:text-green-400 text-xs font-bold uppercase tracking-wider"><Icons.ArrowLeft className="w-3.5 h-3.5" /> Back</button>
      <button type="submit" disabled={loading} data-testid="contact-submit" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold px-7 py-3.5 rounded uppercase text-sm tracking-wider">
        {loading ? <><Icons.Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <>Submit Request <Icons.Send className="w-4 h-4" /></>}
      </button>
    </div>
  </form>
);

/* -------- Done card -------- */
const DoneCard = ({ servicesCount, subsCount, pref, onReset }) => (
  <div className="bg-[#0e1116] text-white rounded-lg p-10 text-center" data-testid="contact-success">
    <Icons.CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
    <h3 className="text-3xl font-black mt-5">Message Sent!</h3>
    <p className="text-gray-300 mt-3 max-w-md mx-auto">Thanks for sharing your project details. Our team will review your selections ({servicesCount} service{servicesCount !== 1 && 's'} · {subsCount} sub-service{subsCount !== 1 && 's'}) and reach out via <span className="text-orange-400 font-bold">{pref}</span> within 24 hours.</p>
    <button onClick={onReset} data-testid="contact-reset" className="mt-6 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded uppercase text-xs tracking-wider">Start a new request</button>
  </div>
);

/* -------- Main container -------- */
const CustomContactForm = () => {
  const { region } = useRegion();
  const { lang } = useLang();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [subs, setSubs] = useState([]);
  const [size, setSize] = useState('');
  const [timeline, setTimeline] = useState('');
  const [pref, setPref] = useState('email');
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [done, setDone] = useState(false);

  const resetAll = () => { setDone(false); setStep(1); setServices([]); setSubs([]); setSize(''); setTimeline(''); setPref('email'); };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErrMsg('');
    const f = new FormData(e.target);
    try {
      await api.contact({
        lead_source: getLeadSource(),
        first_name: f.get('firstName') || '',
        last_name: f.get('lastName') || '',
        email: f.get('email') || '',
        phone: f.get('phone') || '',
        company: f.get('company') || '',
        website: f.get('website') || '',
        requirement: f.get('req') || '',
        region: region?.code || 'global',
        language: lang,
        selected_services: services,
        selected_sub_services: subs,
        project_size: size,
        timeline,
        preferred_contact: pref
      });
      setDone(true);
      e.target.reset();
    } catch (err) {
      setErrMsg(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) return <DoneCard servicesCount={services.length} subsCount={subs.length} pref={pref} onReset={resetAll} />;

  return (
    <div className="bg-[#0e1116] text-white rounded-lg p-6 md:p-10" data-testid="custom-contact-form">
      <ProgressBar step={step} setStep={setStep} />
      {step === 1 && <Step1 services={services} setServices={setServices} setStep={setStep} />}
      {step === 2 && <Step2 services={services} subs={subs} setSubs={setSubs} size={size} setSize={setSize} timeline={timeline} setTimeline={setTimeline} setStep={setStep} />}
      {step === 3 && (
        <Step3
          summary={{ servicesCount: services.length, subsCount: subs.length, size, timeline }}
          pref={pref} setPref={setPref}
          errMsg={errMsg} loading={loading}
          onBack={() => setStep(2)}
          onSubmit={submit}
        />
      )}
    </div>
  );
};

export default CustomContactForm;
